import path from 'path'

import { callMainPrompt } from '@anvil/agent-runtime/main-prompt'
import {
  buildUserMessageContent,
  withSystemTags,
} from '@anvil/agent-runtime/util/messages'
import { MAX_AGENT_STEPS_DEFAULT } from '@anvil/common/constants/agents'
import { dropUnansweredToolCalls } from '@anvil/common/util/messages'
import {
  FILE_READ_STATUS,
  toOptionalFile,
} from '@anvil/common/constants/paths'
import {
  getMCPClient,
  listMCPTools,
  callMCPTool,
} from '@anvil/common/mcp/client'
import {
  COMPOSIO_META_TOOL_NAMES,
  isComposioMetaToolName,
} from '@anvil/common/constants/composio'
import { toolNames } from '@anvil/common/tools/constants'
import { clientToolCallSchema } from '@anvil/common/tools/list'
import { AgentOutputSchema } from '@anvil/common/types/session-state'
import {
  FETCH_IDLE_TIMEOUT_USER_MESSAGE,
  TRANSIENT_NETWORK_ERROR_USER_MESSAGE,
  extractApiErrorDetails,
  isFetchIdleTimeoutError,
  isTransientNetworkError,
} from '@anvil/common/util/error'
import { isSensitiveEnvFilePath } from '@anvil/common/util/env-file-path'
import { cloneDeep } from 'lodash'

import { executeComposioToolViaServer } from './composio'
import { getErrorStatusCode } from './error-utils'
import { getAgentRuntimeImpl } from './impl/agent-runtime'
import { getUserInfoFromApiKey } from './impl/database'
import { initialSessionState, applyOverridesToSessionState } from './run-state'
import type { ComputedProjectIndex } from './run-state'
import { changeFile } from './tools/change-file'
import { applyPatchTool } from './tools/apply-patch'
import { codeSearch } from './tools/code-search'
import { glob } from './tools/glob'
import { listDirectory } from './tools/list-directory'
import { getProjectPathLookupKeys } from './tools/path-utils'
import { getFiles } from './tools/read-files'
import { readUrl } from './tools/read-url'
import { runTerminalCommand } from './tools/run-terminal-command'
import type { TerminalCommandBroker } from './tools/run-terminal-command'

import type { CustomToolDefinition } from './custom-tool'
import type { RunState } from './run-state'
import type { FileFilter } from './tools/read-files'
import type { ServerAction } from '@anvil/common/actions'
import type { FileReadWindow } from '@anvil/common/types/contracts/client'
import type { AgentDefinition } from '@anvil/common/templates/initial-agents-dir/types/agent-definition'
import type { ToolName } from '@anvil/common/tools/constants'
import type { PublishedClientToolName } from '@anvil/common/tools/list'
import type { Logger } from '@anvil/common/types/contracts/logger'
import type {
  AgentUsageData,
  ContextCompactionData,
} from '@anvil/common/types/contracts/llm'
import type { TraceWriter } from '@anvil/common/types/contracts/trace'
import type { AnvilFileSystem } from '@anvil/common/types/filesystem'
import type { ToolMessage } from '@anvil/common/types/messages/anvil-message'
import type { ToolResultOutput } from '@anvil/common/types/messages/content-part'
import type { PrintModeEvent } from '@anvil/common/types/print-mode'
import type { SessionState } from '@anvil/common/types/session-state'
import type { Source } from '@anvil/common/types/source'
import type { AnvilSpawn } from '@anvil/common/types/spawn'

type OverrideToolHandlers = {
  [K in PublishedClientToolName]?: (input: any) => Promise<ToolResultOutput[]>
} & {
  // Include read_files separately, since it has a different signature.
  read_files?: (input: {
    filePaths: string[]
    /** Present only for `windowedFileReads` agents. An override that ignores
     *  it returns whole files, which is a cost regression rather than a
     *  correctness bug — but it is the whole point of the flag, so overrides
     *  on hosted surfaces window the content themselves (they hold the file
     *  before their own read budget truncates it). */
    fileWindows?: Record<string, FileReadWindow[]>
  }) => Promise<Record<string, string | null>>
}

function isRunPauseError(error: unknown) {
  return (
    !!error &&
    typeof error === 'object' &&
    (('anvilRunPaused' in error &&
      (error as { anvilRunPaused?: unknown }).anvilRunPaused === true) ||
      ('name' in error &&
        (error as { name?: unknown }).name === 'AnvilRunPausedError'))
  )
}

export type AnvilClientOptions = {
  apiKey?: string

  cwd?: string
  /** Optional directory path to load skills from. Skills found here will be available to the `skill` tool. */
  skillsDir?: string
  projectFiles?: Record<string, string>
  /** Precomputed index for exactly these `projectFiles` (build it with
   *  `computeProjectIndexFromFiles`). Skips the per-run tree-sitter parse;
   *  ignored when `projectFiles` is absent. */
  projectIndex?: ComputedProjectIndex
  knowledgeFiles?: Record<string, string>
  agentDefinitions?: AgentDefinition[]
  maxAgentSteps?: number
  env?: Record<string, string>
  /** Optional host process boundary that keeps terminal tools away from an
   * interactive console. Headless consumers can omit it. */
  terminalCommandBroker?: TerminalCommandBroker

  handleEvent?: (event: PrintModeEvent) => void | Promise<void>
  handleStreamChunk?: (
    chunk:
      | string
      | {
          type: 'subagent_chunk'
          agentId: string
          agentType: string
          chunk: string
        }
      | {
          type: 'reasoning_chunk'
          agentId: string
          ancestorRunIds: string[]
          chunk: string
        },
  ) => void | Promise<void>

  /** Optional filter to classify files before reading (runs before gitignore check) */
  fileFilter?: FileFilter

  overrideTools?: OverrideToolHandlers
  customToolDefinitions?: CustomToolDefinition[]

  fsSource?: Source<AnvilFileSystem>
  spawnSource?: Source<AnvilSpawn>
  logger?: Logger
  /** Optional debug trace of agent message histories. Called with the full
   *  history at each agent step boundary; implementations should append each
   *  message once (see TraceWriter). */
  traceWriter?: TraceWriter
}

export type ImageContent = {
  type: 'image'
  image: string // base64 encoded
  mediaType: string
}

export type TextContent = {
  type: 'text'
  text: string
}

export type MessageContent = TextContent | ImageContent

export type RunOptions = {
  agent: string | AgentDefinition
  prompt: string
  /** End user represented by this run. Trusted service accounts may use this
   *  to run on behalf of a signed-in user; ordinary API keys cannot delegate. */
  userId?: string
  /** Content array for multimodal messages (text + images) */
  content?: MessageContent[]
  params?: Record<string, any>
  previousRun?: RunState
  extraToolResults?: ToolMessage[]
  signal?: AbortSignal
  /** Optional steering hook. Drained at each agent step boundary during the run;
   * any returned texts are appended to the conversation as user prompts (and keep
   * the turn going) before the next LLM call. Lets a host inject messages into a
   * running agent without aborting — i.e. "steer" it, as opposed to queuing a new
   * prompt for after the turn finishes. */
  drainSteeringMessages?: () => string[]
  costMode?: string
  /** Extra key/values merged into each LLM request's `anvil_metadata`.
   *  Used by hosts (e.g. the CLI) to forward client-scoped identifiers like
   *  `freebuff_instance_id` that server-side gates read from the request body. */
  extraAnvilMetadata?: Record<string, string>
  /** Optional checkpoint hook. Called once when the run starts and then
   * periodically while it is in flight, with a RunState snapshot that
   * preserves all progress so far (the user's prompt plus any completed
   * agent steps, ending with an interruption note). Hosts can persist these
   * snapshots so that a killed process (closed terminal, crash) does not
   * lose the in-flight turn. The final resolved RunState supersedes any
   * snapshot; no snapshots are emitted after the run settles. */
  onStateSnapshot?: (runState: RunState) => void
  /** Provider-reported usage for each root-agent model request. */
  onUsage?: (usage: AgentUsageData) => void
  /** A model request ended before an exact provider usage receipt arrived. */
  onUsageIncomplete?: () => void
  /** Mechanical context compaction performed by the root agent runtime. */
  onCompaction?: (data: ContextCompactionData) => void
}

/** How often onStateSnapshot fires while a run is in flight. */
const STATE_SNAPSHOT_INTERVAL_MS = 5_000

export const STATE_SNAPSHOT_INTERRUPTION_MESSAGE =
  'The session ended before this response completed. Partial progress has been preserved.'

/**
 * Copy a SessionState for a checkpoint / cancellation snapshot: the caller
 * appends an interruption message and must not disturb the live session.
 *
 * lodash cloneDeep of the whole session is expensive — ~230ms on an ~8 MB
 * session — and this runs on the CLI's render/input thread every ~5s at each
 * in-flight snapshot, a major source of long-session freezes.
 *
 * Only mainAgentState needs an independent copy: the mutations that would
 * otherwise bleed into an already-captured snapshot all live there —
 * messageHistory (.push), agentContext subgoals (update_subgoal). We copy it
 * with a JSON round-trip, which is ~50x faster than cloneDeep (~4ms). The
 * snapshot is only ever consumed as JSON — persisted to disk, and re-serialized
 * when fed back as previousRunState — so a JSON round-trip is byte-for-byte
 * parity with the prior cloneDeep→JSON.stringify path. It also can't choke on
 * the URL / Buffer / Uint8Array instances the message schema permits in
 * image/file content (structuredClone throws on URL and rewrites
 * Buffer→Uint8Array; both change the persisted bytes or fall back).
 *
 * fileContext is large, effectively read-only during a run, and already
 * persisted as-is, so we share it by reference rather than copy it.
 *
 * Falls back to cloneDeep only if JSON.stringify throws (circular refs /
 * BigInt — which AgentState isn't expected to contain, as it uses IDs rather
 * than object back-refs) so a snapshot — or the final error state, which shares
 * this path — can never fail to build.
 */
export function cloneSessionState(
  state: SessionState,
  logger?: Logger,
): SessionState {
  let mainAgentState: SessionState['mainAgentState']
  try {
    mainAgentState = JSON.parse(JSON.stringify(state.mainAgentState))
  } catch (error) {
    logger?.debug?.(
      { error: error instanceof Error ? error.message : String(error) },
      'JSON clone of mainAgentState failed; falling back to cloneDeep',
    )
    mainAgentState = cloneDeep(state.mainAgentState)
  }
  return { fileContext: state.fileContext, mainAgentState }
}

const createAbortError = (signal?: AbortSignal) => {
  if (signal?.reason instanceof Error) {
    return signal.reason
  }
  const error = new Error('Aborted')
  error.name = 'AbortError'
  return error
}

type RunExecutionOptions = RunOptions &
  AnvilClientOptions & {
    apiKey: string
    fingerprintId: string
  }
type RunReturnType = RunState

export async function run(options: RunExecutionOptions): Promise<RunState> {
  const { signal } = options

  if (signal?.aborted) {
    const abortError = createAbortError(signal)
    return {
      sessionState: options.previousRun?.sessionState,
      traceSessionId:
        options.previousRun?.traceSessionId ?? crypto.randomUUID(),
      output: {
        type: 'error',
        message: abortError.message,
      },
    }
  }

  return runOnce(options)
}

async function runOnce({
  apiKey,
  fingerprintId,

  cwd,
  skillsDir,
  projectFiles,
  projectIndex,
  knowledgeFiles,
  agentDefinitions,
  maxAgentSteps = MAX_AGENT_STEPS_DEFAULT,
  env,
  terminalCommandBroker,

  handleEvent,
  handleStreamChunk,

  fileFilter,
  overrideTools,
  customToolDefinitions,

  fsSource = () => require('fs').promises,
  spawnSource,
  logger,
  traceWriter,

  agent,
  prompt,
  userId: requestedUserId,
  content,
  params,
  previousRun,
  extraToolResults,
  signal,
  drainSteeringMessages,
  costMode,
  extraAnvilMetadata,
  onStateSnapshot,
  onUsage,
  onUsageIncomplete,
  onCompaction,
}: RunExecutionOptions): Promise<RunState> {
  const fsSourceValue = typeof fsSource === 'function' ? fsSource() : fsSource
  const fs = await fsSourceValue
  let spawn: AnvilSpawn
  if (spawnSource) {
    const spawnSourceValue = await spawnSource
    spawn = spawnSourceValue as AnvilSpawn
  } else {
    spawn = require('child_process').spawn as AnvilSpawn
  }
  let activeCustomToolDefinitions = customToolDefinitions ?? []

  // Init session state
  let agentId
  if (typeof agent !== 'string') {
    const clonedDefs = agentDefinitions ? cloneDeep(agentDefinitions) : []
    agentDefinitions = [...clonedDefs, agent]
    agentId = agent.id
  } else {
    agentId = agent
  }
  let sessionState: SessionState
  if (previousRun?.sessionState) {
    // applyOverridesToSessionState handles deep cloning and applying any provided overrides
    sessionState = await applyOverridesToSessionState(
      cwd,
      previousRun.sessionState,
      {
        knowledgeFiles,
        agentDefinitions,
        customToolDefinitions,
        projectFiles,
        projectIndex,
        maxAgentSteps,
      },
    )
  } else {
    // No previous run, so create a fresh session state
    sessionState = await initialSessionState({
      cwd,
      skillsDir,
      knowledgeFiles,
      agentDefinitions,
      customToolDefinitions,
      projectFiles,
      projectIndex,
      maxAgentSteps,
      fs,
      spawn,
      logger,
    })
  }
  const traceSessionId = previousRun?.traceSessionId ?? crypto.randomUUID()

  for (const toolName of COMPOSIO_META_TOOL_NAMES) {
    delete sessionState.fileContext.customToolDefinitions[toolName]
  }

  let resolvePromise: (value: RunReturnType) => any = () => {}
  let _reject: (error: any) => any = () => {}
  const promise = new Promise<RunReturnType>((res, rej) => {
    resolvePromise = res
    _reject = rej
  })

  // Snapshot support: stop emitting the moment the run settles so a late
  // snapshot can never overwrite the final state persisted by the host.
  let settled = false
  let snapshotTimer: ReturnType<typeof setInterval> | null = null
  const resolve = (value: RunReturnType) => {
    settled = true
    if (snapshotTimer !== null) {
      clearInterval(snapshotTimer)
      snapshotTimer = null
    }
    resolvePromise(value)
  }

  async function onError(error: { message: string }) {
    if (handleEvent) {
      await handleEvent({ type: 'error', message: error.message })
    }
  }

  // The agent runtime mutates sessionState.mainAgentState as it progresses,
  // replacing messageHistory with a new array once it adds the user prompt.
  // Comparing array identity detects progress more robustly than length:
  // context pruning could shrink history below its starting length without
  // meaning the runtime never ran.
  let initialMessageHistory = sessionState.mainAgentState.messageHistory

  /** Calculates the current session state if cancelled.
   *
   * This is used when callMainPrompt throws an error. If the agent runtime made
   * any progress (replaced the shared messageHistory), those messages are
   * preserved. Otherwise the user's message is added so it isn't lost.
   */
  function getCancelledSessionState(message: string): SessionState {
    const runtimeMadeProgress =
      sessionState.mainAgentState.messageHistory !== initialMessageHistory

    const state = cloneSessionState(sessionState, logger)
    // A checkpoint can land after an assistant tool call is recorded but before
    // its result arrives. Drop that half-step at the persistence boundary so a
    // resumed run starts from structurally valid history.
    state.mainAgentState.messageHistory = dropUnansweredToolCalls(
      state.mainAgentState.messageHistory,
    )

    // Only add the user's message if the runtime didn't get a chance to add it.
    if (!runtimeMadeProgress && (prompt || content)) {
      state.mainAgentState.messageHistory.push({
        role: 'user' as const,
        content: buildUserMessageContent(prompt, params, content),
        tags: ['USER_PROMPT'] as string[],
      })
    }

    // Add error context message
    state.mainAgentState.messageHistory.push({
      role: 'user' as const,
      content: [{ type: 'text' as const, text: withSystemTags(message) }],
    })
    return state
  }
  function getCancelledRunState(message?: string): RunState {
    message = message ?? 'Run cancelled by user.'
    return {
      sessionState: getCancelledSessionState(message),
      traceSessionId,
      output: {
        type: 'error',
        message,
      },
    }
  }

  const onResponseChunk = async (
    action: ServerAction<'response-chunk'>,
  ): Promise<void> => {
    if (signal?.aborted) {
      return
    }
    const { chunk } = action

    if (typeof chunk !== 'string') {
      if (chunk.type === 'reasoning_delta') {
        handleStreamChunk?.({
          type: 'reasoning_chunk',
          chunk: chunk.text,
          // The agent's stable id (matches subagent_start/subagent_chunk), so
          // subagent reasoning attributes to the right agent. (Previously this
          // forwarded runId, which no consumer's agent map is keyed by.)
          agentId: chunk.agentId,
          ancestorRunIds: chunk.ancestorRunIds,
        })
      } else {
        await handleEvent?.(chunk)
      }
      return
    }

    if (handleStreamChunk) {
      await handleStreamChunk(chunk)
    }
  }
  const onSubagentResponseChunk = async (
    action: ServerAction<'subagent-response-chunk'>,
  ) => {
    if (signal?.aborted) {
      return
    }
    const { agentId, agentType, chunk } = action

    if (handleStreamChunk && chunk) {
      await handleStreamChunk({
        type: 'subagent_chunk',
        agentId,
        agentType,
        chunk,
      })
    }
  }

  const agentRuntimeImpl = getAgentRuntimeImpl({
    logger,
    traceWriter,
    apiKey,
    handleStepsLogChunk: () => {
      // Does nothing for now
    },
    requestToolCall: async ({ userInputId, toolName, input, mcpConfig }) => {
      return handleToolCall({
        action: {
          type: 'tool-call-request',
          requestId: crypto.randomUUID(),
          userInputId,
          toolName,
          input,
          timeout: undefined,
          mcpConfig,
        },
        overrides: overrideTools ?? {},
        customToolDefinitions: activeCustomToolDefinitions
          ? Object.fromEntries(
              activeCustomToolDefinitions.map((def) => [def.toolName, def]),
            )
          : {},
        cwd,
        fs,
        env,
        terminalCommandBroker,
        apiKey,
        signal,
      })
    },
    requestMcpToolData: async ({ mcpConfig, toolNames }) => {
      const mcpClientId = await getMCPClient(mcpConfig)
      const listToolsResult = await listMCPTools(mcpClientId)
      const tools = listToolsResult.tools
      const filteredTools: typeof tools = []
      for (const tool of tools) {
        if (!toolNames) {
          filteredTools.push(tool)
          continue
        }
        if (toolNames.includes(tool.name)) {
          filteredTools.push(tool)
          continue
        }
      }

      return filteredTools
    },
    requestFiles: ({ filePaths, fileWindows }) =>
      readFiles({
        filePaths,
        fileWindows,
        override: overrideTools?.read_files,
        fileFilter,
        cwd,
        fs,
      }),
    requestOptionalFile: async ({ filePath }) => {
      const files = await readFiles({
        filePaths: [filePath],
        override: overrideTools?.read_files,
        fileFilter,
        cwd,
        fs,
        // str_replace/write_file use this path to compute edits. A truncated
        // read makes exact matches later in large files impossible.
        limitContent: false,
        enforceEnvPolicy: false,
      })
      const lookupKeys = cwd
        ? getProjectPathLookupKeys(cwd, filePath)
        : [filePath]
      const fileKey = lookupKeys.find((key) => key in files)
      return toOptionalFile(fileKey === undefined ? null : files[fileKey]!)
    },
    sendAction: ({ action }) => {
      if (action.type === 'action-error') {
        onError({ message: action.message })
        return
      }
      if (action.type === 'response-chunk') {
        onResponseChunk(action)
        return
      }
      if (action.type === 'subagent-response-chunk') {
        onSubagentResponseChunk(action)
        return
      }
      if (action.type === 'prompt-response') {
        handlePromptResponse({
          action,
          resolve,
          onError,
          initialSessionState: sessionState,
          traceSessionId,
        })
        return
      }
      if (action.type === 'prompt-error') {
        handlePromptResponse({
          action,
          resolve,
          onError,
          initialSessionState: sessionState,
          traceSessionId,
        })
        return
      }
    },
    sendSubagentChunk: ({
      userInputId,
      agentId,
      agentType,
      chunk,
      prompt,
      forwardToPrompt = true,
    }) => {
      onSubagentResponseChunk({
        type: 'subagent-response-chunk',
        userInputId,
        agentId,
        agentType,
        chunk,
        prompt,
        forwardToPrompt,
      })
    },
  })

  const promptId = Math.random().toString(36).substring(2, 15)

  // Send input
  const userInfo = await getUserInfoFromApiKey({
    ...agentRuntimeImpl,
    apiKey,
    fields: ['id'],
  })
  if (!userInfo) {
    return getCancelledRunState('Invalid API key or user not found')
  }
  const authenticatedUserId = userInfo.id
  const userId = requestedUserId ?? authenticatedUserId

  if (signal?.aborted) {
    return getCancelledRunState('Run cancelled by user.')
  }

  if (onStateSnapshot) {
    // The runtime replaces mainAgentState.messageHistory with a new array at
    // each step boundary, so reference identity is a cheap "has anything
    // durable changed" check. Skipping unchanged ticks avoids deep-cloning a
    // potentially multi-MB sessionState every interval while the run is just
    // waiting on a slow LLM call.
    let lastSnapshotHistory: unknown = null
    const emitStateSnapshot = () => {
      if (settled || signal?.aborted) {
        return
      }
      const history = sessionState.mainAgentState.messageHistory
      if (history === lastSnapshotHistory) {
        return
      }
      lastSnapshotHistory = history
      try {
        onStateSnapshot(
          getCancelledRunState(STATE_SNAPSHOT_INTERRUPTION_MESSAGE),
        )
      } catch (error) {
        logger?.debug?.(
          { error: error instanceof Error ? error.message : String(error) },
          'onStateSnapshot handler threw',
        )
      }
    }
    // Emit immediately so the user's prompt is checkpointed as soon as the
    // run starts, then keep checkpointing progress while it is in flight.
    emitStateSnapshot()
    snapshotTimer = setInterval(emitStateSnapshot, STATE_SNAPSHOT_INTERVAL_MS)
    // Don't let the checkpoint timer keep the host process alive.
    if (typeof (snapshotTimer as any).unref === 'function') {
      ;(snapshotTimer as any).unref()
    }
  }

  const report = <T>(callback: ((value: T) => void) | undefined) =>
    callback
      ? (value: T) => {
          try {
            callback(value)
          } catch (error) {
            agentRuntimeImpl.logger.debug?.(
              { error: error instanceof Error ? error.message : String(error) },
              'Run metrics handler threw',
            )
          }
        }
      : undefined
  const reportSignal = (callback: (() => void) | undefined) =>
    callback
      ? () => {
          try {
            callback()
          } catch (error) {
            agentRuntimeImpl.logger.debug?.(
              { error: error instanceof Error ? error.message : String(error) },
              'Run metrics handler threw',
            )
          }
        }
      : undefined

  callMainPrompt({
    ...agentRuntimeImpl,
    promptId,
    action: {
      type: 'prompt',
      promptId,
      prompt,
      promptParams: params,
      content,
      fingerprintId: fingerprintId,
      costMode: costMode ?? 'normal',
      sessionState,
      toolResults: extraToolResults ?? [],
      agentId,
    },
    drainSteeringMessages,
    repoUrl: undefined,
    repoId: undefined,
    clientSessionId: promptId,
    userId,
    extraAnvilMetadata: {
      ...(extraAnvilMetadata ?? {}),
      trace_session_id: traceSessionId,
    },
    signal: signal ?? new AbortController().signal,
    onAgentUsageReceived: report(onUsage),
    onAgentUsageIncomplete: reportSignal(onUsageIncomplete),
    onCompaction: report(onCompaction),
  }).catch((error) => {
    let errorMessage = isFetchIdleTimeoutError(error)
      ? FETCH_IDLE_TIMEOUT_USER_MESSAGE
      : isTransientNetworkError(error)
        ? TRANSIENT_NETWORK_ERROR_USER_MESSAGE
        : error instanceof Error
          ? error.message
          : String(error ?? '')
    const apiErrorDetails = extractApiErrorDetails(error)
    const statusCode = apiErrorDetails.statusCode ?? getErrorStatusCode(error)
    const {
      countryBlockReason,
      countryCode,
      errorCode,
      ipPrivacySignals,
      message: parsedMessage,
    } = apiErrorDetails
    if (parsedMessage) {
      errorMessage = parsedMessage
    }

    resolve({
      sessionState: getCancelledSessionState(errorMessage),
      traceSessionId,
      output: {
        type: 'error',
        message: errorMessage,
        ...(statusCode !== undefined && { statusCode }),
        ...(errorCode !== undefined && { error: errorCode }),
        ...(countryCode !== undefined && { countryCode }),
        ...(countryBlockReason !== undefined && { countryBlockReason }),
        ...(ipPrivacySignals !== undefined && { ipPrivacySignals }),
      },
    })
  })

  return promise
}

function requireCwd(cwd: string | undefined, toolName: string): string {
  if (!cwd) {
    throw new Error(
      `cwd is required for the ${toolName} tool. Please provide cwd in AnvilClientOptions or override the ${toolName} tool.`,
    )
  }
  return cwd
}

async function readFiles({
  filePaths,
  fileWindows,
  override,
  fileFilter,
  cwd,
  fs,
  limitContent,
  enforceEnvPolicy = true,
}: {
  filePaths: string[]
  fileWindows?: Record<string, FileReadWindow[]>
  override?: NonNullable<
    Required<AnvilClientOptions>['overrideTools']['read_files']
  >
  fileFilter?: FileFilter
  cwd?: string
  fs: AnvilFileSystem
  limitContent?: boolean
  enforceEnvPolicy?: boolean
}) {
  if (override) {
    // Windows are forwarded, not applied here: an override reads from a remote
    // workspace and applies its own output budget, so windowing after it has
    // already truncated a large file would answer an offset past the cut-off
    // with "beyond the end of the file". The override windows before its own
    // limiter, in the same order the local path does.
    if (!enforceEnvPolicy) {
      return await override({
        filePaths,
        ...(fileWindows ? { fileWindows } : {}),
      })
    }

    const result = Object.create(null) as Record<string, string | null>
    const readablePaths: string[] = []
    for (const filePath of filePaths) {
      if (!filePath) continue
      if (isSensitiveEnvFilePath(filePath)) {
        result[filePath] = FILE_READ_STATUS.IGNORED
      } else {
        readablePaths.push(filePath)
      }
    }
    if (readablePaths.length > 0) {
      const loadedFiles = await override({
        filePaths: readablePaths,
        ...(fileWindows ? { fileWindows } : {}),
      })
      if (
        !loadedFiles ||
        typeof loadedFiles !== 'object' ||
        Array.isArray(loadedFiles)
      ) {
        return { ...result }
      }
      for (const [filePath, content] of Object.entries(loadedFiles)) {
        result[filePath] = isSensitiveEnvFilePath(filePath)
          ? FILE_READ_STATUS.IGNORED
          : content
      }
    }
    return { ...result }
  }
  return getFiles({
    filePaths,
    cwd: requireCwd(cwd, 'read_files'),
    fs,
    fileWindows,
    fileFilter,
    limitContent,
    enforceEnvPolicy,
  })
}

async function handleToolCall({
  action,
  overrides,
  customToolDefinitions,
  cwd,
  fs,
  env,
  terminalCommandBroker,
  apiKey,
  signal,
}: {
  action: ServerAction<'tool-call-request'>
  overrides: NonNullable<AnvilClientOptions['overrideTools']>
  customToolDefinitions: Record<string, CustomToolDefinition>
  cwd?: string
  fs: AnvilFileSystem
  env?: Record<string, string>
  terminalCommandBroker?: TerminalCommandBroker
  apiKey: string
  signal?: AbortSignal
}): Promise<{ output: ToolResultOutput[] }> {
  const toolName = action.toolName
  const input = action.input

  if (signal?.aborted) {
    return {
      output: [
        {
          type: 'json',
          value: {
            message: 'Tool call cancelled: the run was aborted by the user.',
          },
        },
      ],
    }
  }

  // Handle MCP tool calls when mcpConfig is present
  if (action.mcpConfig) {
    try {
      const mcpClientId = await getMCPClient(action.mcpConfig)
      const result = await callMCPTool(
        mcpClientId,
        {
          name: toolName,
          arguments: input,
        },
        undefined,
        signal ? { signal } : undefined,
      )
      return { output: result }
    } catch (error) {
      return {
        output: [
          {
            type: 'json',
            value: {
              errorMessage:
                error instanceof Error ? error.message : String(error),
            },
          },
        ],
      }
    }
  }

  let result: ToolResultOutput[]
  if (toolNames.includes(toolName as ToolName)) {
    clientToolCallSchema.parse(action)
  } else {
    const customToolHandler = customToolDefinitions[toolName]

    if (!customToolHandler) {
      throw new Error(
        `Custom tool handler not found for user input ID ${action.userInputId}`,
      )
    }
    return {
      output: await customToolHandler.execute(action.input),
    }
  }

  try {
    let override = overrides[toolName as PublishedClientToolName]
    if (
      !override &&
      (toolName === 'str_replace' || toolName === 'apply_patch')
    ) {
      // Reuse the write_file override for file editing tools.
      override = overrides['write_file']
    }
    if (override) {
      // Note: This type assertion is necessary because TypeScript cannot narrow
      // the union type of all possible tool inputs based on the dynamic toolName.
      // The input has been validated by clientToolCallSchema.parse above.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result = await override(input as any)
    } else if (toolName === 'end_turn') {
      result = [{ type: 'json', value: { message: 'Turn ended.' } }]
    } else if (toolName === 'write_file' || toolName === 'str_replace') {
      result = await changeFile({
        parameters: input,
        cwd: requireCwd(cwd, toolName),
        fs,
      })
    } else if (toolName === 'apply_patch') {
      result = await applyPatchTool({
        parameters: input,
        cwd: requireCwd(cwd, toolName),
        fs,
      })
    } else if (toolName === 'run_terminal_command') {
      const resolvedCwd = requireCwd(cwd, 'run_terminal_command')
      result = await runTerminalCommand({
        ...input,
        cwd: path.resolve(resolvedCwd, input.cwd ?? '.'),
        env,
        signal,
        terminalCommandBroker,
      } as Parameters<typeof runTerminalCommand>[0])
    } else if (toolName === 'read_url') {
      result = await readUrl({
        ...(input as Parameters<typeof readUrl>[0]),
        signal,
      })
    } else if (toolName === 'code_search') {
      result = await codeSearch({
        projectPath: requireCwd(cwd, 'code_search'),
        ...input,
        signal,
      } as Parameters<typeof codeSearch>[0])
    } else if (toolName === 'list_directory') {
      result = await listDirectory({
        directoryPath: (input as { path: string }).path,
        projectPath: requireCwd(cwd, 'list_directory'),
        fs,
      })
    } else if (toolName === 'glob') {
      const globInput = input as {
        pattern: string
        cwd?: string
        max_results?: number
      }
      result = await glob({
        pattern: globInput.pattern,
        projectPath: requireCwd(cwd, 'glob'),
        cwd: globInput.cwd,
        maxResults: globInput.max_results,
        fs,
      })
    } else if (toolName === 'run_file_change_hooks') {
      // No-op: SDK doesn't run file change hooks
      result = [
        {
          type: 'json',
          value: {
            message: 'File change hooks are not supported in SDK mode',
          },
        },
      ]
    } else if (isComposioMetaToolName(toolName)) {
      result = await executeComposioToolViaServer({
        apiKey,
        toolName,
        input,
      })
    } else {
      throw new Error(
        `Tool not implemented in SDK. Please provide an override or modify your agent to not use this tool: ${toolName}`,
      )
    }
  } catch (error) {
    if (isRunPauseError(error)) {
      throw error
    }

    result = [
      {
        type: 'json',
        value: {
          errorMessage:
            error &&
            typeof error === 'object' &&
            'message' in error &&
            typeof error.message === 'string'
              ? error.message
              : typeof error === 'string'
                ? error
                : 'Unknown error',
        },
      },
    ]
  }
  return {
    output: result,
  }
}

/**
 * Extracts an HTTP status code from an error message string.
 * Parses common error patterns to identify the underlying status code.
 * Returns the status code if found, undefined otherwise.
 */
export const extractStatusCodeFromMessage = (
  errorMessage: string,
): number | undefined => {
  const lowerMessage = errorMessage.toLowerCase()

  // AI SDK's built-in retry error (e.g., "Failed after 4 attempts. Last error: Service Unavailable")
  // The AI SDK already retried 4 times, but we still want our SDK wrapper to retry 3 more times
  if (
    lowerMessage.includes('failed after') &&
    lowerMessage.includes('attempts')
  ) {
    // Extract the underlying error type from the message
    if (lowerMessage.includes('service unavailable')) {
      return 503
    }
    if (lowerMessage.includes('timeout')) {
      return 408
    }
    if (lowerMessage.includes('connection refused')) {
      return 503
    }
    // Default to 500 for other AI SDK retry failures
    return 500
  }

  if (
    errorMessage.includes('503') ||
    lowerMessage.includes('service unavailable')
  ) {
    return 503
  }
  if (errorMessage.includes('504')) {
    return 504
  }
  if (errorMessage.includes('502')) {
    return 502
  }
  if (lowerMessage.includes('timeout') || errorMessage.includes('408')) {
    return 408
  }
  if (
    lowerMessage.includes('econnrefused') ||
    lowerMessage.includes('connection refused')
  ) {
    return 503
  }
  if (lowerMessage.includes('dns') || lowerMessage.includes('enotfound')) {
    return 503
  }
  if (lowerMessage.includes('server error') || errorMessage.includes('500')) {
    return 500
  }
  if (errorMessage.includes('429') || lowerMessage.includes('rate limit')) {
    return 429
  }
  if (
    lowerMessage.includes('network error') ||
    lowerMessage.includes('fetch failed')
  ) {
    return 503
  }

  return undefined
}

async function handlePromptResponse({
  action,
  resolve,
  onError,
  initialSessionState,
  traceSessionId,
}: {
  action: ServerAction<'prompt-response'> | ServerAction<'prompt-error'>
  resolve: (value: RunReturnType) => any
  onError: (error: { message: string }) => void
  initialSessionState: SessionState
  traceSessionId: string
}) {
  if (action.type === 'prompt-error') {
    onError({ message: action.message })

    const statusCode = extractStatusCodeFromMessage(action.message)
    resolve({
      sessionState: initialSessionState,
      traceSessionId,
      output: {
        type: 'error',
        message: action.message,
        ...(statusCode !== undefined && { statusCode }),
      },
    })
  } else if (action.type === 'prompt-response') {
    // Stop enforcing session state schema! It's a black box we will pass back to the server.
    // Only check the output schema.
    const parsedOutput = AgentOutputSchema.safeParse(action.output)
    if (!parsedOutput.success) {
      const message = [
        'Received invalid prompt response from server:',
        JSON.stringify(parsedOutput.error.issues),
        'If this issues persists, please contact support@codebuff.com',
      ].join('\n')
      onError({ message })
      resolve({
        sessionState: initialSessionState,
        traceSessionId,
        output: {
          type: 'error',
          message,
        },
      })
      return
    }
    const { sessionState, output } = action

    const state: RunState = {
      sessionState,
      traceSessionId,
      output: output ?? {
        type: 'error',
        message: 'No output from agent',
      },
    }
    resolve(state)
  } else {
    action satisfies never
    onError({
      message: 'Internal error: prompt response type not handled',
    })
    resolve({
      sessionState: initialSessionState,
      traceSessionId,
      output: {
        type: 'error',
        message: 'Internal error: prompt response type not handled',
      },
    })
  }
}
